/**
 * Plugin audit trail.
 *
 * Every plugin invocation writes a row: {plugin_id, study_id, moment,
 * action, latency_ms, cost, result_status, error, timestamp}. The host
 * posts audit rows to the sidecar's /audit endpoint; the sidecar is
 * responsible for persistence (SQL Server img_plugin_audit for Day 2+;
 * SQLite / in-memory for the Manual Priority Tagger stub).
 *
 * Rationale for sidecar-mediated rather than frontend-direct SQL:
 *   - Frontend never has DB credentials
 *   - Sidecar can enforce schema + append-only semantics
 *   - Each plugin owns its own audit backend (SaMD vs utility vendors
 *     have very different retention + encryption requirements)
 */

import type { LoadedRuntimePlugin, TriggerMoment } from './types';

export interface AuditRow {
  plugin_id: string;
  study_id: string;
  moment: TriggerMoment | 'result_fetch' | 'bulk' | 'manual';
  action: 'send' | 'skip' | 'no_match' | 'error';
  latency_ms?: number;
  cost?: number;         // snapshot of per-call cost at time of invocation
  result_status?: string;
  error?: string;
}

/** Fire-and-forget audit write. Never throws — audit failures must not
 *  block the actual plugin call. */
export async function writeAudit(
  plugin: LoadedRuntimePlugin,
  row: AuditRow,
): Promise<void> {
  try {
    await fetch(`${plugin.baseUrl}/audit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...row, timestamp: new Date().toISOString() }),
    });
  } catch {
    // Audit swallow — real failures surface via the main call path.
  }
}
